import React, { Component } from "react";
import CloseIcon from "@material-ui/icons/Close";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import LazyImage from "../LazyImage/LazyImage";
import constants from "../Constants/constants";
import { categorizePreviewCards, getUniqueCards, getTurnCurve, getElementDistribution, getSetPurchaseUrl } from "./deckUtils";

export default class DeckPreviewModal extends Component {
  render() {
    const {
      previewDeck, darkMode, showRarityEffects,
      previewGroupByTurn, onToggleGroupByTurn,
      testHandActive, testHandCards, testHandRemaining,
      testHandSelected,
      onTestHandDraw, onTestHandDrawOne, onTestHandClose,
      onTestHandToggleSelect, onTestHandMulligan,
      onOpenModal, onClose,
    } = this.props;

    if (!previewDeck) return null;

    const themeClass = darkMode ? "modal-dark" : "modal-light";
    const cards = previewDeck.cards || [];
    const { characters, strategies, abilities, field } = categorizePreviewCards(cards);
    const nonFieldCards = cards.filter(c => c.cardType !== "Field");
    const nonFieldCount = nonFieldCards.length;
    const elementDist = getElementDistribution(cards);
    const uniqueElements = Object.keys(elementDist);

    const elementColors = {
      Fire: "#e63946", Water: "#457b9d", Terra: "#2a9d8f",
      Air: "#e9c46a", Plasma: "#9b5de5", Necro: "#555"
    };

    const charCount = characters.reduce((s, e) => s + e.count, 0);
    const stratCount = strategies.reduce((s, e) => s + e.count, 0);
    const abilCount = abilities.reduce((s, e) => s + e.count, 0);

    // Composition bar percentages
    const total = charCount + stratCount + abilCount || 1;
    const charPct = Math.round((charCount / total) * 100);
    const stratPct = Math.round((stratCount / total) * 100);
    const abilPct = 100 - charPct - stratPct;

    // Turn curve data
    const turnCurve = getTurnCurve(cards);
    const turnKeys = Object.keys(turnCurve).sort((a, b) => Number(a) - Number(b));
    const maxTurnCount = Math.max(...Object.values(turnCurve), 1);

    const renderSection = (title, entries, count, groupByTurn = false) => {
      if (entries.length === 0) return null;

      if (groupByTurn) {
        // Group entries by turn count
        const turnGroups = {};
        for (const entry of entries) {
          const turn = entry.card.turnCount !== undefined ? entry.card.turnCount : "?";
          if (!turnGroups[turn]) turnGroups[turn] = [];
          turnGroups[turn].push(entry);
        }
        const sortedTurns = Object.keys(turnGroups).sort((a, b) => Number(a) - Number(b));

        return (
          <div className="preview-section">
            <h3 className="preview-section-title">{title} <span className="preview-section-badge">{count}</span></h3>
            {sortedTurns.map(turn => {
              const group = turnGroups[turn];
              const groupTotal = group.reduce((s, e) => s + e.count, 0);
              return (
                <div key={turn} className="preview-turn-group">
                  <div className="preview-turn-label">Turn {turn} <span className="preview-turn-count">({groupTotal})</span></div>
                  <div className="preview-card-grid">
                    {group.flatMap(({ card, count: copies }) =>
                      Array.from({ length: copies }, (_, i) => (
                        <div key={`${card._id}-${i}`} className="preview-card" onClick={() => onOpenModal(card, true)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpenModal(card, true); } }} aria-label={`View ${card.name}`}>
                          <LazyImage src={`${constants.DOMAIN}${card.image}`} alt={card.name} eager />
                        </div>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        );
      }

      return (
        <div className="preview-section">
          <h3 className="preview-section-title">{title} <span className="preview-section-badge">{count}</span></h3>
          <div className="preview-card-grid">
            {entries.flatMap(({ card, count: copies }) =>
              Array.from({ length: copies }, (_, i) => (
                <div key={`${card._id}-${i}`} className="preview-card" onClick={() => onOpenModal(card, true)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpenModal(card, true); } }} aria-label={`View ${card.name}`}>
                  <LazyImage src={`${constants.DOMAIN}${card.image}`} alt={card.name} eager />
                </div>
              ))
            )}
          </div>
        </div>
      );
    };

    return (
      <div className={`db-preview-overlay ${themeClass}`} onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="preview-deck-name">
        <div className={`db-preview-modal ${themeClass}`} onClick={(e) => e.stopPropagation()}>
          <button className="db-preview-close-btn" onClick={onClose} aria-label="Close preview">
            <CloseIcon />
          </button>

          {/* Hero banner */}
          <div className="preview-hero">
            {previewDeck.image ? (
              <img
                className="preview-hero-image"
                src={`${constants.DOMAIN}${previewDeck.image}`}
                alt={`${previewDeck.name} deck cover`}
              />
            ) : (
              <div className="preview-hero-placeholder" />
            )}
            <div className="preview-hero-info">
              <h1 className="preview-hero-name" id="preview-deck-name">{previewDeck.name}</h1>
              <p className="preview-hero-subtitle">
                {nonFieldCount}/{nonFieldCount < 50 ? 50 : 60} cards{field.length > 0 ? ` · ${field.length} field` : ""} · {uniqueElements.length} element{uniqueElements.length !== 1 ? "s" : ""}
              </p>
            </div>
          </div>

          {/* Stats bar */}
          {cards.length > 0 && (
            <div className="preview-stats">
              <div className="preview-stats-types">
                <div className="preview-stat-item">
                  <span className="preview-stat-label">Characters</span>
                  <span className="preview-stat-value">{charCount}</span>
                </div>
                <div className="preview-stat-item">
                  <span className="preview-stat-label">Strategies</span>
                  <span className="preview-stat-value">{stratCount}</span>
                </div>
                <div className="preview-stat-item">
                  <span className="preview-stat-label">Abilities</span>
                  <span className="preview-stat-value">{abilCount}</span>
                </div>
              </div>

              {/* Composition bar */}
              <div className="preview-comp-bar">
                {charCount > 0 && <div className="comp-segment comp-char" style={{ width: `${charPct}%` }} title={`Characters ${charPct}%`} />}
                {stratCount > 0 && <div className="comp-segment comp-strat" style={{ width: `${stratPct}%` }} title={`Strategies ${stratPct}%`} />}
                {abilCount > 0 && <div className="comp-segment comp-abil" style={{ width: `${abilPct}%` }} title={`Abilities ${abilPct}%`} />}
              </div>

              {/* Element distribution */}
              <div className="preview-elements">
                {uniqueElements.map(el => (
                  <span key={el} className="preview-element-tag" style={{
                    backgroundColor: `${elementColors[el] || "#888"}22`,
                    color: elementColors[el] || "#888",
                    borderColor: `${elementColors[el] || "#888"}44`
                  }}>
                    {el}: {elementDist[el]}
                  </span>
                ))}
              </div>

              {/* Turn curve */}
              {turnKeys.length > 0 && (
                <div className="preview-turn-curve">
                  <span className="preview-turn-curve-label">Turn Curve</span>
                  <div className="turn-curve-chart">
                    {turnKeys.map(turn => {
                      const count = turnCurve[turn];
                      const heightPct = Math.round((count / maxTurnCount) * 100);
                      return (
                        <div key={turn} className="turn-curve-col" title={`Turn ${turn}: ${count} cards`}>
                          <div className="turn-curve-bar-wrap">
                            <div className="turn-curve-bar" style={{ height: `${heightPct}%` }} />
                          </div>
                          <span className="turn-curve-value">{count}</span>
                          <span className="turn-curve-label">T{turn}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Shop sets */}
              {(() => {
                const sets = [...new Set(cards.map(c => c.cardSet).filter(Boolean))];
                const setsWithUrls = sets.filter(s => getSetPurchaseUrl(s));
                if (setsWithUrls.length === 0) return null;
                return (
                  <div className="preview-sets">
                    <span className="preview-sets-label">Cards are from these sets:</span>
                    <div className="preview-sets-links">
                      {setsWithUrls.map(setName => (
                        <a
                          key={setName}
                          className="preview-set-link"
                          href={getSetPurchaseUrl(setName)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {setName} 🛒
                        </a>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Deck content */}
          <div className="preview-body">
            {cards.length > 0 && (
              <div className="preview-view-options">
                <label className="preview-toggle">
                  <input
                    type="checkbox"
                    checked={previewGroupByTurn}
                    onChange={(e) => onToggleGroupByTurn(e.target.checked)}
                  />
                  <span className="preview-toggle-slider" />
                  <span className="preview-toggle-text">Group by Turn</span>
                </label>
                <button
                  className="test-hand-btn"
                  onClick={onTestHandDraw}
                >
                  {testHandActive ? "Reshuffle" : "Test Hand"}
                </button>
              </div>
            )}

            {/* Test Hand Panel */}
            {testHandActive && (
              <div className="test-hand-panel">
                <div className="test-hand-header">
                  <div className="test-hand-header-left">
                    <h3 className="test-hand-title">Test Hand</h3>
                    <span className="test-hand-count">
                      {testHandCards.length} drawn · {testHandRemaining.length} remaining
                    </span>
                  </div>
                  <button className="test-hand-close" onClick={onTestHandClose} aria-label="Close test hand">
                    <CloseIcon style={{ fontSize: 18 }} />
                  </button>
                </div>
                <div className="test-hand-scroll-wrap">
                  <button
                    className="test-hand-scroll-btn test-hand-scroll-left"
                    onClick={() => { const el = document.querySelector(".test-hand-cards"); if (el) el.scrollBy({ left: -200, behavior: "smooth" }); }}
                    aria-label="Scroll left"
                  >
                    <ChevronLeftIcon />
                  </button>
                  <div className="test-hand-cards">
                    <div className="test-hand-cards-inner">
                      {testHandCards.map((card, i) => {
                        const isSelected = testHandSelected && testHandSelected.has(i);
                        return (
                          <div
                            key={`hand-${card._id}-${i}`}
                            className={`test-hand-card ${isSelected ? "test-hand-card-selected" : ""}`}
                            onClick={() => onTestHandToggleSelect(i)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onTestHandToggleSelect(i); } }}
                            aria-label={`${isSelected ? "Deselect" : "Select"} ${card.name} for mulligan`}
                          >
                            <LazyImage src={`${constants.DOMAIN}${card.image}`} alt={card.name} eager />
                            {isSelected && <div className="test-hand-card-check">✓</div>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <button
                    className="test-hand-scroll-btn test-hand-scroll-right"
                    onClick={() => { const el = document.querySelector(".test-hand-cards"); if (el) el.scrollBy({ left: 200, behavior: "smooth" }); }}
                    aria-label="Scroll right"
                  >
                    <ChevronRightIcon />
                  </button>
                </div>
                <div className="test-hand-actions">
                  <button className="test-hand-redraw-btn" onClick={onTestHandDraw}>
                    Redraw
                  </button>
                  <button
                    className="test-hand-mulligan-btn"
                    onClick={onTestHandMulligan}
                    disabled={!testHandSelected || testHandSelected.size === 0}
                  >
                    Mulligan{testHandSelected && testHandSelected.size > 0 ? ` (${testHandSelected.size})` : ""}
                  </button>
                  <button
                    className="test-hand-draw-btn"
                    onClick={onTestHandDrawOne}
                    disabled={testHandRemaining.length === 0}
                  >
                    Draw Card ({testHandRemaining.length} left)
                  </button>
                </div>
              </div>
            )}

            {cards.length === 0 ? (
              <p className="preview-empty">This deck is empty.</p>
            ) : (
              <div className="preview-layout">
                {/* Field card sidebar */}
                {field.length > 0 && (
                  <div className="preview-field-sidebar">
                    <h3 className="preview-section-title">Field <span className="preview-section-badge">{field.length}</span></h3>
                    {field.map(({ card }) => (
                      <div key={card._id} className="preview-field-card" onClick={() => onOpenModal(card, true)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpenModal(card, true); } }} aria-label={`View ${card.name}`}>
                        <LazyImage src={`${constants.DOMAIN}${card.image}`} alt={card.name} eager />
                      </div>
                    ))}
                  </div>
                )}

                {/* Main deck sections */}
                <div className="preview-main">
                  {renderSection("Characters", characters, charCount, previewGroupByTurn)}
                  {renderSection("Strategies", strategies, stratCount, previewGroupByTurn)}
                  {renderSection("Abilities", abilities, abilCount)}

                  {/* Side deck */}
                  {(() => {
                    const sideDeck = previewDeck.sideDeck || [];
                    if (sideDeck.length === 0) return null;
                    const sideUnique = getUniqueCards(sideDeck);
                    return (
                      <div className="preview-section preview-side-deck">
                        <h3 className="preview-section-title">Side Deck <span className="preview-section-badge">{sideDeck.length}</span></h3>
                        <div className="preview-card-grid">
                          {sideUnique.flatMap(({ card, count: copies }) =>
                            Array.from({ length: copies }, (_, i) => (
                              <div key={`side-${card._id}-${i}`} className="preview-card" onClick={() => onOpenModal(card, true)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpenModal(card, true); } }} aria-label={`View ${card.name}`}>
                                <LazyImage src={`${constants.DOMAIN}${card.image}`} alt={card.name} eager />
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>

        </div>
      </div>
    );
  }
}
